import React, { useState, useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Switch,
  createTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMainContentLogic } from '../Content component/sanskritContent/SanskritMainContentLogics';
import { useEnglishMainContentLogic } from '../Content component/englishContent/EnglishMainContentLogics';
import './App.css';
import '../Content component/sanskritContent/fonts/siddhanta.ttf';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';  // Corrected import
import HomeIcon from '@mui/icons-material/Home';

//----------------------Defining TypeScript interfaces for data structure----------------------
interface Shloka {
  taranga_heading: string;
  shloka: string[];
  Authorline: string;
}

interface Taranga {
  taranga_heading: string; 
  shloka: string[];
}

interface Lambaka {
  lambaka_heading: string;
  taranga: Taranga[];
}

interface Data {
  body: {
    page: {
      lambaka: Lambaka[];
    };
  };
}

const SanskritMainContent: React.FC = () => {

  // ----------------------Custom hooks for main content logic, including data and theme states----------------------
  const {
    data,
    error,
    open,
    darkMode,
    handleDrawerToggle,
    handleThemeChange,
    handleTarangaNavigation,
  } = useMainContentLogic();

  const {
    englishData,
    englishError,
    englishDarkMode,
    englishHandleThemeChange,
  } = useEnglishMainContentLogic();

  // ----------------------State to control expanded sections and selected indices----------------------
  const [expandedLambaka, setExpandedLambaka] = useState<number | null>(null);
  const [selectedTarangaIndex, setSelectedTarangaIndex] = useState<number | null>(null);
  const [currentLambakaIndex, setCurrentLambakaIndex] = useState<number | null>(0);
  const [selectedBook, setSelectedBook] = useState<number | null>(0);
  const [currentChapter, setCurrentChapter] = useState<number | null>(0);
    const [showBackToTop, setShowBackToTop] = useState(false);

  // ----------------------Define theme, setting dark or light mode----------------------
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
    typography: {
      fontFamily: 'Siddhanta, Roboto, Helvetica, Arial, sans-serif',
    },
  });

  // ----------------------Set default selection for Sanskrit content upon data loading----------------------
  useEffect(() => {
    if (data?.body?.page?.lambaka && data.body.page.lambaka.length > 0) {
      setCurrentLambakaIndex(0);
      setSelectedTarangaIndex(0);
    }
  }, [data]);

  // ----------------------Set default selection for English content upon data loading----------------------
  useEffect(() => {
    if (englishData?.body?.page?.book && englishData.body.page.book.length > 0) {
      setSelectedBook(0);
      setCurrentChapter(0);
    }
  }, [englishData]);

  // ----------------------Handling the expanding or collapsing of Accordion for lambaka----------------------
  const handleLambakaAccordionChange = (index: number) => {
    setExpandedLambaka(expandedLambaka === index ? null : index);
  };

    // ----------------------Scroll to top function----------------------
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };


// ----------------------Function to handle Scroll event----------------------
const handleScroll = () => {
  if (window.scrollY > 300) {
    setShowBackToTop(true);
  } else {
    setShowBackToTop(false);
  }
};

    // ----------------------Adding Scroll Event Listener on mount----------------------
    if (typeof window !== "undefined") {
      window.addEventListener('scroll', handleScroll);
    }
    
// ----------------------Removing Scroll Event Listener on unmount----------------------
const removeScrollListener = () => {
  if (typeof window !== "undefined") {
    window.removeEventListener('scroll', handleScroll);
  }
};

  // ----------------------Updating selected taranga and toggle drawer for navigation----------------------
  const handleTarangaClick = (lambakaIndex: number, tarangaIndex: number) => {
    handleTarangaNavigation(tarangaIndex);
    setCurrentLambakaIndex(lambakaIndex);
    setSelectedTarangaIndex(tarangaIndex);
    
    const selectedLambaka = data?.body?.page?.lambaka[lambakaIndex];
    if (selectedLambaka && selectedLambaka.taranga[tarangaIndex]) {
      setSelectedBook(lambakaIndex);
      setCurrentChapter(tarangaIndex);
    }
    handleDrawerToggle();
  };

  // ----------------------Rendering paragraphs of shlokas or stories----------------------
  const renderParagraphs = (paragraphs: string[] | string | undefined) => {
    if (!paragraphs) return null;
    if (typeof paragraphs === 'string') {
      return (
        <Typography variant="body1" paragraph>
          {paragraphs}
        </Typography>
      );
    }
    return paragraphs.map((para, index) => (
      <Typography key={index} variant="body1" paragraph>
        {para}
      </Typography>
    ));
  };

  // ----------------------Render story content, iterating through paragraphs or sections----------------------
  const renderStoryContent = (story: any) => {
    if (Array.isArray(story)) {
      return story.map((item, index) => item?.p && renderParagraphs(item.p));
    } else if (story?.p) {
      return renderParagraphs(story.p);
    }
    return null;
  };

  // ----------------------Render structured story sections with titles and content----------------------
  const renderStorySection = (story: any, title: string) =>
    story ? (
      <>
        <Divider />
        <Typography variant="h5">{title}</Typography>
        {renderStoryContent(story)}
      </>
    ) : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* ----------------------Navbar---------------------- */}
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Katha Saritsagara
          </Typography>
          <Switch checked={darkMode} onChange={handleThemeChange} color="default" />
          <Link to="/">
          <button className="homeBtn" style={{ display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center"}}>
            <HomeIcon style={{ display:"flex",  fontSize:"31px" }} /> {/* This is the HomeIcon */}
            <span style={{display:"block", fontSize:"10px"}}>Home</span>
          </button>
          </Link>
        </Toolbar>
      </AppBar>

      {/* ----------------------Menu section on the navbar---------------------- */}
      <Drawer anchor="left" open={open} onClose={handleDrawerToggle}>
        <div className="drawerHeading">Explore Books</div>
        <List>
          {data?.body?.page?.lambaka?.map((lambaka: Lambaka, lambakaIndex: number) => (
            <div key={lambakaIndex}>
              <Accordion
                expanded={expandedLambaka === lambakaIndex}
                onChange={() => handleLambakaAccordionChange(lambakaIndex)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{lambaka.lambaka_heading}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {lambaka.taranga?.map((taranga, tarangaIndex) => (
                      <ListItem
                        key={tarangaIndex}
                        button
                        selected={selectedTarangaIndex === tarangaIndex && currentLambakaIndex === lambakaIndex}
                        onClick={() => handleTarangaClick(lambakaIndex, tarangaIndex)}
                      >
                        <ListItemText primary={taranga.taranga_heading} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              <Divider />
            </div>
          ))}
        </List>
      </Drawer>

      <Container maxWidth={false} style={{ padding: '2rem', display: 'flex' }}>
        <Grid container spacing={2}>

          {/*----------------------Sanskrit content rendering----------------------*/}
          <Grid item xs={12} md={6}>
          <Card
  className="Section"
  variant="outlined"
  style={{
    width: '100%',
    margin: '0',
    height: 'calc(100vh - 64px)', // Dynamically set card height (AppBar height subtracted)
    display: 'flex',
    flexDirection: 'column',
  }}
>
  <CardContent style={{ padding: '0px', flex: 1, overflow: 'hidden' }}>
    {error && <Typography variant="body1" color="error">{error}</Typography>}
    {!error && !data && <Typography variant="body1">Loading data...</Typography>}
    {currentLambakaIndex !== null && data?.body?.page?.lambaka && (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Non-Scrollable Heading */}
        <div>
          <Typography
            variant="h4"
            gutterBottom
            className="LambakaHeading"
          >
            {data.body.page.lambaka[currentLambakaIndex]?.lambaka_heading || 'Select a Taranga'}
          </Typography>
          {selectedTarangaIndex !== null && (
            <Typography
              variant="h5"
              className="TarangaHeading"
              style={{ marginBottom: '10px' }}
            >
              {data.body.page.lambaka[currentLambakaIndex]?.taranga[selectedTarangaIndex]?.taranga_heading}
            </Typography>
          )}
        </div>

        {/* Scrollable Content */}
        {selectedTarangaIndex !== null &&
          data.body.page.lambaka[currentLambakaIndex]?.taranga.map((taranga: Taranga, tarangaIndex: number) => {
            if (tarangaIndex === selectedTarangaIndex) {
              return (
                <div
                  key={tarangaIndex}
                  className="scrollable-content" // Add this class for styling
                >
                  {taranga.shloka.map((shlokaText: string, shlokaIndex: number) => (
                    <Typography
                      key={shlokaIndex}
                      variant="body1"
                      style={{ marginBottom: '8px' }}
                    >
                      {shlokaText}
                    </Typography>
                  ))}
                </div>
              );
            }
          })}
      </div>
    )}
  </CardContent>
</Card>



          </Grid>

          {/*----------------------English content rendering----------------------*/}
          <Grid item xs={12} md={6}>
  {selectedBook !== null && currentChapter !== null && englishData?.body?.page?.book?.[selectedBook]?.chapter?.[currentChapter] && (
    <Card
    variant="outlined"
    style={{
      height: 'calc(100vh - 64px)', // Ensures the card takes up available vertical space
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <CardContent
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden', // Prevent overflow from spilling
        padding: '16px',
      }}
    >
      {/* Static Content */}
      <Typography className="BookTitle" variant="h4">
        {englishData.body.page.book[selectedBook].title_book}
      </Typography>
      <Typography
        variant="h5"
        style={{ marginBottom: '10px' }}
        className="ChapterTitleHeading"
      >
        {englishData.body.page.book[selectedBook].chapter[currentChapter].title_chap}
      </Typography>
  
      {/* Scrollable Box */}
      <Box
        className="scrollable-content" // Apply the class to hide scrollbar
        style={{
          flex: 1, // Ensures this section takes up remaining space in CardContent
          overflowY: 'auto', // Enables vertical scrolling
          padding: '0px 16px 16px 16px',
        }}
      >
        {/* INVOCATION content */}
        {Array.isArray(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.INVOCATION?.p)
          ? englishData.body.page.book[selectedBook].chapter[currentChapter].INVOCATION.p.map((para: string, index: number) => (
              <Typography key={index} variant="body1" paragraph>
                {para}
              </Typography>
            ))
          : renderParagraphs(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.p)}
  
        <Divider />
        {englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.mainstory &&
          renderStoryContent(englishData.body.page.book[selectedBook].chapter[currentChapter].mainstory)}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1, 'Story 1')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1?.story1a, 'Story 1a')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story1?.story1b, 'Story 1b')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2, 'Story 2')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2a, 'Story 2a')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2b, 'Story 2b')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2d, 'Story 2d')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story2?.story2e, 'Story 2e')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story3, 'Story 3')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story3?.story3a, 'Story 3a')}
        {renderStorySection(englishData.body.page.book[selectedBook]?.chapter[currentChapter]?.story4, 'Story 4')}
      </Box>
    </CardContent>
  </Card>
  
  )}
</Grid>

        </Grid>
      </Container>

            {/* ----------------------Back to Top button---------------------- */}
      {/* ----------------------Back to Top Button---------------------- */}
      {showBackToTop && (
  <Button
    style={{ position: 'fixed', bottom: '16px', right: '16px', backgroundColor:"#00796b", color:"white" , border:"none"}}
    onClick={scrollToTop}
  >
    Back to Top
  </Button>
)}
    </ThemeProvider>
  );
};

export default SanskritMainContent;
